import { useState, useEffect, useMemo, useCallback } from "react";
import { useFilterContext } from "./ContextManager";

import style from "../css/filter.module.css";
import "../index.css";

import logo from "../assets/logo/NEA-dark-horizontal-small.png";

//Variables
import { countriesListObj } from "../utils/variables";
import { isoLatLong } from "../utils/variables";

//utility functions
import {
  debounce,
  airportIdMap,
  timeConverter,
  createAirportRouteCountMap,
  calculateCO2,
} from "../utils/utilityFunctions";

import { getCustomAirports } from "../utils/customAirportFunctions";

import {
  fetchAndStoreTimestampAndCountriesList,
  getCountryData,
} from "../utils/indexedDBFunctions";

import { createAirportsSummary } from "../utils/createSummary";
import { createCountrySummary } from "../utils/countrySummary";

//components
import { MenuStats } from "./SubComponents/MenuStats/MenuStats";
import { CustomSlider } from "./UX/CustomSlider/CustomSlider";
import { CustomPopper } from "./UX/CustomPopper/CustomPopper";
import { RestoreDefaultValuesButton } from "./UX/RestoreDefaultValuesButton/RestoreDefaultValuesButton";
import { CustomAirportList } from "./CustomAirportList";
import { PlaneProfiles } from "./PlaneProfiles";
import { ClearCountriesButton } from "./UX/ClearCountriesButton/ClearCountriesButton";
import { DisclaimerModal } from "./DisclaimerModal";
import { FeedbackModal } from "./FeedbackModal";
import { AdvancedSettingsModal } from "./SubComponents/AdvancedSettings/AdvancedSettings";
import { AdminMenuToggle } from "./SubComponents/AdminMenuToggle/AdminMenuToggle";
import { CustomAirportManagerModal } from "./SubComponents/CustomAirportManagerModal/CustomAirportManagerModal";

//materialUI components
import Switch from "@mui/material/Switch";
import Fab from "@mui/material/Fab";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip"; // For rendering selected countries with a removal icon
import CancelIcon from "@mui/icons-material/Cancel"; // Import the icon for removal
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";

import FilterTooltip from "./UX/FilterTooltip/FilterTooltip";

export function Filter({ map }) {
  const {
    filters,
    updateFilters,
    airportsData,
    setAirportsData,
    routesData,
    setRoutesData,
    setFilteredAirportsData,
    setFilteredRoutesData,
    selectedAirports,
    selectedRoutes,
    updateAirportIdMap,
    lastMapCenter,
    lastMapZoom,
    updateLoadingState,
    updateAirportRouteCountMap,
    customAirports,
    updateCustomAirports,
    paxCount,
    updatePaxCount,
    updateAirportsSummary,
    updateCountrySummary,
    passengerLoadFactor,
  } = useFilterContext();

  // States for storing original and user-filtered data
  const [isTimeCountryReady, setIsTimeCountryReady] = useState(false);
  const [countriesList, setCountriesList] = useState([]);
  const [autocompleteValue, setAutocompleteValue] = useState(null); // To control the country autocomplete input value
  const [inputText, setInputText] = useState(""); // useState to control the input text
  // const [customAirports, setCustomAirports] = useState([]);
  const [airportRouteCountMap, setAirportRouteCountMap] = useState({});
  const [customAirportManagerOpen, setCustomAirportManagerOpen] =
    useState(false);

  const [sliderValues, setSliderValues] = useState({
    minDistance: filters.minDistance,
    maxDistance: filters.maxDistance,
    minRunwayLength: filters.minRunwayLength,
    maxRunwayLength: filters.maxRunwayLength,
    timeSavedValue: filters.timeSavedValue,
    // ratioValue: filters.ratioValue,
    // speed: filters.speed,
    // departure: filters.departure,
    // arrival: filters.arrival,
    userSizeMultiplier: filters.userSizeMultiplier,
    savedCO2Value: filters.savedCO2Value,
  });

  // Storing only certain values from filters that will be used as dependencies for route useEffects and useMemos
  const relevantRouteFilters = useMemo(() => {
    const {
      minDistance,
      maxDistance,
      routeType,
      commercial,
      pso,
      routesPax,
      compareRoutes,
      // ratioActive,

      // ratioValue,
      timeSavedValue,
      speed,
      departure,
      arrival,
      savedCO2Active,
      savedCO2Value,
      savedCO2YearValue,
      // Add any other properties needed
    } = filters;

    return {
      minDistance,
      maxDistance,
      routeType,
      commercial,
      pso,
      routesPax,
      compareRoutes,
      // ratioActive,

      // ratioValue,
      timeSavedValue,
      speed,
      departure,
      arrival,
      savedCO2Active,
      savedCO2Value,
      savedCO2YearValue,
    };
  }, [
    filters.minDistance,
    filters.maxDistance,
    filters.routeType,
    filters.commercial,
    filters.pso,
    filters.routesPax,
    filters.compareRoutes,
    // filters.ratioActive,

    // filters.ratioValue,
    filters.timeSavedValue,
    filters.speed,
    filters.departure,
    filters.arrival,
    filters.savedCO2Active,
    filters.savedCO2Value,
    filters.savedCO2YearValue,
  ]);

  // Storing only certain values from filters that will be used as dependencies for airports useEffects and useMemos
  const relevantAirportFilters = useMemo(() => {
    const {
      selectedAirports,
      airportPax,
      country,
      custom,
      maxRunwayLength,
      minRunwayLength,
      scheduledService,
      type,
      // ratioActive,
      timeSavedActive,
      // Add any other properties needed
    } = filters;

    return {
      selectedAirports,
      airportPax,
      country,
      custom,
      maxRunwayLength,
      minRunwayLength,
      scheduledService,
      type,
      // ratioActive,
      timeSavedActive,
    };
  }, [
    filters.selectedAirports,
    filters.airportPax,
    filters.country,
    filters.custom,
    filters.maxRunwayLength,
    filters.minRunwayLength,
    filters.scheduledService,
    filters.type,
    // filters.ratioActive,
    filters.timeSavedActive,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      await fetchAndStoreTimestampAndCountriesList();
      setIsTimeCountryReady(true);
    };

    fetchData();
  }, []);

  const [menuClasses, setMenuClasses] = useState(
    `${style.filterMenu} ${style.menuStart}`,
  );
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  useEffect(() => {
    // Function to fetch countries list from IndexedDB
    if (isTimeCountryReady) {
      // Convert the countries list object to an array of { code, label } objects
      const countries = Object.entries(countriesListObj).map(
        ([code, label]) => ({
          code: code.replace(/_/g, "-"), // Replace "_" with "-" in code
          label,
        }),
      );
      // Sort the array alphabetically by the label
      countries.sort((a, b) => a.code.localeCompare(b.code));
      setCountriesList(countries);
    }
  }, [isTimeCountryReady]);

  //Set custom user defined airports
  useEffect(() => {
    const update = async () => {
      updateCustomAirports(await getCustomAirports());
    };
    update();
  }, []);

  // useEffect to fetch Main Data
  useEffect(() => {
    const initializeData = async () => {
      try {
        updateLoadingState(true); // Start loading
        let fetchedData = await getCountryData(filters.country);
        let fetchedAirportsData = [];
        let fetchedRoutesData = [];

        if (fetchedData.airports && fetchedData.airports.length > 0) {
          fetchedAirportsData = fetchedData.airports;
        }

        if (fetchedData.routes && fetchedData.routes.length > 0) {
          fetchedRoutesData = fetchedData.routes;
        }

        if (
          customAirports &&
          customAirports.airports &&
          customAirports.airports.length > 0
        ) {
          fetchedAirportsData = fetchedAirportsData.concat(
            customAirports.airports,
          );
          fetchedRoutesData = fetchedRoutesData.concat(customAirports.routes);
        }

        setAirportsData(fetchedAirportsData);
        updateAirportIdMap(airportIdMap(fetchedAirportsData));

        const routesDataWithCO2 = fetchedRoutesData.map((route) => {
          const ATR42 = calculateCO2(route.distance, passengerLoadFactor);
          let ES30 = { totalCO2_kg: 0, CO2_perPassenger: 0 };
          // If the route is longer than 200km, calculate CO2 emissions for the ES-30
          if (route.distance > 200) {
            ES30 = calculateCO2(
              route.distance,
              passengerLoadFactor,
              30,
              "ES30",
            );
          }

          const savedCO2_perPax = Math.max(
            0,
            ATR42.CO2_perPassenger - ES30.CO2_perPassenger,
          );
          const savedCO2_kgPerYear = savedCO2_perPax * (route.pax || 0);

          return {
            ...route,
            ATR42_CO2: ATR42,
            ES30_CO2: ES30,
            savedCO2_perPax,
            savedCO2_kgPerYear,
          };
        });
        setRoutesData(routesDataWithCO2);
      } catch (error) {
        console.error("Error fetching data:", error);
        // Handle any errors e.g., by setting error state or notifying the user
      } finally {
        updateLoadingState(false); // Stop loading regardless of success or failure
      }
    };

    if (isTimeCountryReady) {
      initializeData();
    }
  }, [
    filters.country,
    isTimeCountryReady,
    customAirports,
    passengerLoadFactor,
  ]);

  useEffect(() => {
    updateAirportRouteCountMap(airportRouteCountMap);
  }, [airportRouteCountMap]);

  // useEffect(() => {
  //   updatePaxCount(totalPax);
  // }, [totalPax]);

  // useMemo for computationally intensive operations
  const filteredAirports = useMemo(() => {
    // Check if airportsData is not null before filtering
    return airportsData ? applyFiltersToAirports(airportsData, filters) : [];
  }, [airportsData, relevantAirportFilters, customAirports]);

  // useMemo for computationally intensive operations
  const {
    filteredRoutes,
    routeCount: newAirportRouteCountMap,
    paxCount: newPaxCount,
  } = useMemo(() => {
    return routesData
      ? applyFiltersToRoutes(
          routesData,
          filters,
          filteredAirports,
          selectedAirports,
        )
      : { filteredRoutes: [], routeCount: {}, paxCount: 0 };
  }, [routesData, relevantRouteFilters, filteredAirports, selectedAirports]);

  const {
    filteredAirportsView,
    filteredRoutesView,
    routeCountView,
    paxCountView,
  } = useMemo(() => {
    // Base case: normal view
    if (!filters.selectionView) {
      return {
        filteredAirportsView: filteredAirports,
        filteredRoutesView: filteredRoutes,
        routeCountView: newAirportRouteCountMap,
        paxCountView: newPaxCount,
      };
    }

    // --- SELECTED VIEW LOGIC ---

    const selectedAirportIds = new Set(
      (selectedAirports || []).map((a) => a.id),
    );
    const selectedRouteIds = new Set(filters.selectedRoutes || []);

    let filteredRoutesView = [];

    if (selectedRouteIds.size > 0) {
      // If any routes are explicitly selected: show ONLY those
      filteredRoutesView = filteredRoutes.filter((r) =>
        selectedRouteIds.has(r._id),
      );
    } else if (selectedAirportIds.size > 0) {
      // If no selected routes, fall back to routes touching selected airports
      filteredRoutesView = filteredRoutes.filter(
        (r) =>
          selectedAirportIds.has(r.airport_1) ||
          selectedAirportIds.has(r.airport_2),
      );
    } else {
      // No selection at all: nothing to show
      return {
        filteredAirportsView: [],
        filteredRoutesView: [],
        routeCountView: {},
        paxCountView: null,
      };
    }

    // Airports to keep: selected airports + endpoints of the selected routes
    const neededAirportIds = new Set(selectedAirportIds);
    for (const r of filteredRoutesView) {
      neededAirportIds.add(r.airport_1);
      neededAirportIds.add(r.airport_2);
    }

    const filteredAirportsView = filteredAirports.filter((a) =>
      neededAirportIds.has(a.airportId),
    );

    // Recompute routeCount & paxCount for this view
    const routeCountView = createAirportRouteCountMap(filteredAirportsView);
    let paxCountView = 0;

    for (const r of filteredRoutesView) {
      routeCountView[r.airport_1] = (routeCountView[r.airport_1] || 0) + 1;
      routeCountView[r.airport_2] = (routeCountView[r.airport_2] || 0) + 1;
      if (r.pax) paxCountView += r.pax;
    }

    if (paxCountView === 0) paxCountView = null;

    return {
      filteredAirportsView,
      filteredRoutesView,
      routeCountView,
      paxCountView,
    };
  }, [
    filters.selectionView,
    filters.selectedRoutes,
    filteredAirports,
    filteredRoutes,
    newAirportRouteCountMap,
    newPaxCount,
    selectedAirports,
  ]);

  // Update context when filtered data changes
  useEffect(() => {
    setFilteredAirportsData(filteredAirportsView);
    updateAirportsSummary(createAirportsSummary(filteredAirportsView));
  }, [filteredAirportsView, selectedAirports]);

  useEffect(() => {
    updatePaxCount(paxCountView);
    setAirportRouteCountMap(routeCountView);
    setFilteredRoutesData(filteredRoutesView);
  }, [filteredRoutesView, routeCountView, paxCountView]);

  useEffect(() => {
    updateCountrySummary(
      createCountrySummary(filteredAirportsView, filteredRoutesView),
    );
  }, [filteredAirportsView, filteredRoutesView]);

  // Function to apply filters to airports
  function applyFiltersToAirports(airports, filters) {
    // Add a check to ensure airports is an array, when fetching new data the data is read as an object with nested objects
    // for a few seconds until it is stored properly. this should be fixed
    if (!Array.isArray(airports)) {
      console.error("Expected an array of airports, received:", airports);
    }

    // If no airport types are selected, return an empty array
    if (filters.type.length === 0 || filters.country.length === 0) {
      return [];
    }

    return airports.filter((airport) => {
      const typeMatch =
        filters.type.length === 0 || filters.type.includes(airport.type);

      const countryMatch =
        filters.country.length > 0 &&
        (filters.country.includes(airport.country?.iso ?? "") ||
          filters.country.includes(airport.region?.iso ?? ""));

      // Bypass all checks if airport.custom exists and is true
      // ❗ Modified: custom airports must still match country
      if (filters.type.includes("custom") && airport.type === "custom") {
        return typeMatch && countryMatch;
      }

      const serviceMatch =
        filters.scheduledService.length === 0 ||
        filters.scheduledService.includes(Boolean(airport.scheduledService));

      let runwayLengthMatch = false;
      if (airport.runways) {
        const runwayLengths = airport.runways.map((runway) => runway.length_m);
        const longestRunwayLength = Math.max(...runwayLengths);
        runwayLengthMatch =
          airport.type === "seaplane" ||
          (longestRunwayLength >= filters.minRunwayLength &&
            longestRunwayLength <= filters.maxRunwayLength);
      }

      // Check for runway length criteria bypass for seaplanes or evaluate runway length for other types

      const airportPaxMatch =
        !filters.airportPax || airport.pax >= filters.airportPax;

      return (
        typeMatch &&
        countryMatch &&
        serviceMatch &&
        runwayLengthMatch &&
        airportPaxMatch
      );
    });
  }

  // Function to apply filters to routes
  function applyFiltersToRoutes(
    routes,
    filters,
    filteredAirports,
    selectedAirports,
  ) {
    if (!Array.isArray(routes)) {
      console.error("Expected an array of routes, received:", routes);
      return { filteredRoutes: [], routeCount: {}, paxCount: 0 };
    }

    if (filters.type.length === 0) {
      return { filteredRoutes: [], routeCount: {}, paxCount: 0 };
    }

    // Initialize route count for all airport IDs
    let routeCount = createAirportRouteCountMap(filteredAirports);

    // Initialize total pax count
    let paxCounter = 0;

    // Parse distance filters
    const minDistance =
      filters.minDistance !== undefined
        ? parseFloat(filters.minDistance)
        : -Infinity;
    const maxDistance =
      filters.maxDistance !== undefined
        ? parseFloat(filters.maxDistance)
        : Infinity;

    if (minDistance === 0 && maxDistance === 0) {
      return { filteredRoutes: [], routeCount, paxCount: 0 };
    }

    const filteredAirportIds = new Set(
      filteredAirports.map((airport) => airport.airportId),
    );
    const filteredRoutes = [];

    const selectedIds = new Set((selectedAirports || []).map((a) => a.id));
    const hasSelection = selectedIds.size > 0;

    for (const route of routes) {
      const routeDistance = parseFloat(route.distance);

      const airport1Included = filteredAirportIds.has(route.airport_1);
      const airport2Included = filteredAirportIds.has(route.airport_2);
      const commercialRoutes =
        !filters.commercial || route.commercial === true || route.pso === true;
      const psoRoutes = !filters.pso || (filters.pso && route.pso === true);
      const routesPaxMatch =
        !filters.routesPax || route.pax >= filters.routesPax;
      const routeTypeMatch =
        !filters.routeType || filters.routeType.includes(route.type);

      const co2Match =
        !filters.savedCO2Active ||
        route.savedCO2_perPax >= filters.savedCO2Value;

      let travelTimeComparison = !filters.compareRoutes; // when OFF, don't filter by time
      let timeSaved = null;

      if (filters.compareRoutes && route.travel) {
        const timeArray = ["car", "transit"]
          .map((type) => route.travel[type])
          .filter((time) => Number.isFinite(time));

        if (timeArray.length) {
          const shortestTime = Math.min(...timeArray);
          const flightTime =
            (route.travel.city || 0) + // guard in case it's missing
            filters.departure +
            filters.arrival +
            Math.ceil((route.distance / filters.speed) * 60);

          timeSaved = shortestTime - flightTime;

          if (Number.isFinite(timeSaved)) {
            route.timeSaved = timeSaved;
            const minSaved = Number(filters.timeSavedValue) || 0;
            travelTimeComparison = timeSaved >= minSaved; // when ON, apply threshold
          }
        }
      }

      const selectionMatch =
        !hasSelection ||
        selectedIds.has(route.airport_1) ||
        selectedIds.has(route.airport_2);

      const minSavedT = filters.savedCO2YearValue || 0; // in tonnes
      const minSavedKg = minSavedT * 1000; // to kg
      const co2YearMatch = route.savedCO2_kgPerYear >= minSavedKg;

      const routePassesFilter =
        routeDistance >= minDistance &&
        routeDistance <= maxDistance &&
        airport1Included &&
        airport2Included &&
        routeTypeMatch &&
        travelTimeComparison &&
        (filters.commercial && filters.pso
          ? commercialRoutes || psoRoutes
          : commercialRoutes && psoRoutes) &&
        routesPaxMatch &&
        co2Match &&
        co2YearMatch &&
        selectionMatch;

      if (routePassesFilter) {
        routeCount[route.airport_1] = (routeCount[route.airport_1] || 0) + 1;
        routeCount[route.airport_2] = (routeCount[route.airport_2] || 0) + 1;
        if (route.pax) {
          paxCounter += route.pax;
        }

        filteredRoutes.push(route);
      }
    }

    return {
      filteredRoutes,
      routeCount,
      paxCount: paxCounter > 0 ? paxCounter : null,
    };
  }

  useEffect(() => {
    if (lastMapCenter) {
      updateFilters((prevFilters) => ({
        ...prevFilters,
        mapCenter: lastMapCenter,
      }));
    }
  }, [lastMapCenter]);

  useEffect(() => {
    if (lastMapZoom) {
      updateFilters((prevFilters) => ({
        ...prevFilters,
        mapZoom: lastMapZoom,
      }));
    }
  }, [lastMapZoom]);

  // Handlers for filter changes
  function handleChange(e, key) {
    // Extract 'checked' status directly from the event target
    const checked = e.target.checked;

    // Initialize the variable to hold the parsed or original value
    let value = e.target.value;
    try {
      // Try to parse the value as JSON to handle boolean and numeric values properly
      value = JSON.parse(value);
    } catch (error) {
      // If JSON parsing fails, retain the original string value
    }

    // Update the filters state
    updateFilters((prevFilters) => {
      let newValue;

      // Check if the current filter is an array
      if (Array.isArray(prevFilters[key])) {
        // If it's an array and the checkbox is checked, add the value to the array
        if (checked) {
          newValue = [...prevFilters[key], value];
        } else {
          // If the checkbox is not checked, remove the value from the array
          newValue = prevFilters[key].filter((item) => item !== value);
        }
      } else if (
        typeof prevFilters[key] === "boolean" &&
        typeof value === "boolean"
      ) {
        // For boolean filters, toggle the state based on the 'checked' status and the parsed value
        newValue = checked ? value : !value;
      } else {
        // For other data types, simply set or clear the value based on 'checked'
        newValue = checked ? value : null;
      }

      // Return the updated filters object with the new value for the specified key
      return { ...prevFilters, [key]: newValue };
    });
  }

  //updating filters.colors with values from color picker, debounce to avoid excessive rerenders of map markers
  const debouncedHandleColorChange = useCallback(
    debounce((type, color, styleIndex) => {
      updateFilters((prevFilters) => ({
        ...prevFilters,
        colors: {
          ...prevFilters.colors,
          [type]: prevFilters.colors[type].map((c, index) =>
            index === styleIndex ? color : c,
          ),
        },
      }));
    }, 200),
    [],
  );

  const handleColorChange = (type, color, styleIndex = filters.colorIndex) => {
    debouncedHandleColorChange(type, color, styleIndex);
  };

  useEffect(() => {
    setSliderValues({
      ...sliderValues,
      minDistance: filters.minDistance,
      maxDistance: filters.maxDistance,
      minRunwayLength: filters.minRunwayLength,
      maxRunwayLength: filters.maxRunwayLength,
      timeSavedValue: filters.timeSavedValue,
      // ratioValue: filters.ratioValue,
      // speed: filters.speed,
      // departure: filters.departure,
      // arrival: filters.arrival,
    });
  }, [
    filters.minDistance,
    filters.maxDistance,
    filters.minRunwayLength,
    filters.maxRunwayLength,
    filters.timeSavedValue,
    // filters.ratioValue,
    // filters.speed,
    // filters.departure,
    // filters.arrival,
  ]);

  const updateSliderValue = (newValue, keys) => {
    let newSliderValues = { ...sliderValues };
    keys.forEach((key, i) => {
      newSliderValues[key] = newValue[i];
    });
    setSliderValues(newSliderValues);
  };

  const debounceOnSliderChangeComitted = useCallback(
    debounce((newValue, keys) => {
      let newFilterValues = { ...filters };
      keys.forEach((key, i) => {
        newFilterValues[key] = newValue[i];
      });
      updateFilters(newFilterValues);
    }, 250),
  );

  const onSliderChangeCommitted = (newValue, keys) => {
    // Update filters state when slider change is committed
    debounceOnSliderChangeComitted(newValue, keys);
  };

  //DISABLED

  const domesticInternationalDisabled = () => {
    //disabled switches for routes
    return (
      !filters.routeType.includes("domestic") &&
      !filters.routeType.includes("international")
    );
  };

  const airportsDisabled = () => {
    return (
      !filters.type.includes("large") &&
      !filters.type.includes("medium") &&
      !filters.type.includes("small") &&
      !filters.type.includes("seaplane")
    );
  };

  const toggleMenu = () => {
    if (menuIsOpen) {
      // Replace menuOpen with menuClose
      setMenuClasses(`${style.filterMenu} ${style.menuClose}`);
    } else {
      // Replace menuClose with menuOpen
      setMenuClasses(`${style.filterMenu} ${style.menuOpen}`);
    }
    // Toggle the state
    setMenuIsOpen(!menuIsOpen);
  };

  const moveToLocation = (lat, lng, zoom = null) => {
    setTimeout(() => {
      const location = new google.maps.LatLng(lat, lng);
      map.panTo(location);
      if (zoom !== null) {
        map.setZoom(zoom);
      }
    }, 500); // 50ms delay
  };

  const handleCountrySelect = (event, newValue) => {
    if (newValue && !filters.country.includes(newValue.code)) {
      updateFilters((prevFilters) => ({
        ...prevFilters,
        country: [...prevFilters.country, newValue.code],
      }));

      // Retrieve coordinates and pan to location
      const coords = isoLatLong[newValue.code];
      if (coords) {
        const [lat, lng] = coords.split(",").map(Number);
        moveToLocation(lat, lng, 6); // No zoom specified, will use current zoom
      }
    }
    setInputText("");
  };

  // Function to handle the removal of a country from the selected list
  const handleCountryRemove = (countryCode) => {
    updateFilters((prevFilters) => ({
      ...prevFilters,
      country: prevFilters.country.filter((code) => code !== countryCode),
    }));
  };

  useEffect(() => {}, [filters.travelType]);

  return (
    <>
      <div className={style.menuButton}>
        <Fab
          color="primary"
          aria-label="open-menu"
          onClick={toggleMenu}
          sx={{ width: "2.5rem", height: "2.5rem" }}
        >
          <MenuIcon className={style.menuIcon} />
        </Fab>
      </div>
      <div className={menuClasses}>
        <div className={style.header}>
          <div className={style.logoCloseButtonContainer}>
            <img src={logo} alt="NEA Logo" />
            <div className={style.menuCloseButton}>
              <IconButton
                aria-label="close menu"
                size="small"
                onClick={toggleMenu}
              >
                <CloseIcon style={{ color: "rgba(125, 125, 125, 0.9)" }} />
              </IconButton>
            </div>
          </div>
          {/* Menu Stats */}
          <MenuStats
            airportsCount={filteredAirports.length}
            routesCount={filteredRoutes.length}
            paxCount={paxCount}
          />
        </div>

        <div className={style.menuContentWrapper}>
          <div className={style.menuContent}>
            {" "}
            <label>
              <FilterTooltip id="distance">
                <div className={style.rangeSliderTitleWrapper}>
                  <span>Distance (km)</span>
                  <span className={style.rangeSliderValues}>
                    {filters.minDistance}km - {filters.maxDistance}km
                  </span>
                </div>
              </FilterTooltip>
              <CustomSlider
                sliderValue={[
                  sliderValues.minDistance,
                  sliderValues.maxDistance,
                ]}
                filterKeys={["minDistance", "maxDistance"]}
                updateSliderValue={updateSliderValue}
                onSliderChangeCommitted={onSliderChangeCommitted}
                step={10}
                max={400}
              />
            </label>
            <label>
              <FilterTooltip id="length">
                <div className={style.rangeSliderTitleWrapper}>
                  <span>Runway Length (m)</span>
                  <span className={style.rangeSliderValues}>
                    {filters.minRunwayLength}m - {filters.maxRunwayLength}m
                  </span>
                </div>
              </FilterTooltip>
              <CustomSlider
                sliderValue={[
                  sliderValues.minRunwayLength,
                  sliderValues.maxRunwayLength,
                ]}
                filterKeys={["minRunwayLength", "maxRunwayLength"]}
                updateSliderValue={updateSliderValue}
                onSliderChangeCommitted={onSliderChangeCommitted}
                step={10}
                max={6000}
              />
            </label>
            <PlaneProfiles />
            {/* AIRPORTS */}
            <div className={style.typeSection}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <p>Airports</p>
                </AccordionSummary>
                <AccordionDetails>
                  <div className={`${style.typeSection} ${style.gap}`}>
                    {" "}
                    <div>
                      {" "}
                      <div className={style.typeContainer}>
                        <label>
                          <Switch
                            value="large"
                            checked={filters.type.includes("large")}
                            onChange={(e) => handleChange(e, "type")}
                          />
                          <FilterTooltip id="large">
                            <span>Large</span>
                          </FilterTooltip>
                        </label>
                        <input
                          type="color"
                          className={style.typeColorPicker}
                          value={filters.colors.large[filters.colorIndex]} //default color from filters state
                          onChange={(e) =>
                            handleColorChange(
                              "large",
                              e.target.value,
                              filters.colorIndex,
                            )
                          }
                        />
                      </div>
                      <div className={style.typeContainer}>
                        <label>
                          <Switch
                            value="medium"
                            checked={filters.type.includes("medium")}
                            onChange={(e) => handleChange(e, "type")}
                          />
                          <FilterTooltip id="medium">
                            <span>Medium</span>
                          </FilterTooltip>
                        </label>
                        <input
                          type="color"
                          className={style.typeColorPicker}
                          value={filters.colors.medium[filters.colorIndex]} //default color from filters state
                          onChange={(e) =>
                            handleColorChange(
                              "medium",
                              e.target.value,
                              filters.colorIndex,
                            )
                          }
                        />
                      </div>
                      <div className={style.typeContainer}>
                        <label>
                          <Switch
                            value="small"
                            checked={filters.type.includes("small")}
                            onChange={(e) => handleChange(e, "type")}
                          />
                          <FilterTooltip id="small">
                            <span>Small</span>
                          </FilterTooltip>
                        </label>
                        <input
                          type="color"
                          className={style.typeColorPicker}
                          value={filters.colors.small[filters.colorIndex]} //default color from filters state
                          onChange={(e) =>
                            handleColorChange(
                              "small",
                              e.target.value,
                              filters.colorIndex,
                            )
                          }
                        />
                      </div>
                      <div className={style.typeContainer}>
                        <label>
                          <Switch
                            value="seaplane"
                            checked={filters.type.includes("seaplane")}
                            onChange={(e) => handleChange(e, "type")}
                          />
                          <FilterTooltip id="seaplane">
                            <span>Seaplane</span>
                          </FilterTooltip>
                        </label>
                        <input
                          type="color"
                          className={style.typeColorPicker}
                          value={filters.colors.seaplane[filters.colorIndex]} //default color from filters state
                          onChange={(e) =>
                            handleColorChange(
                              "seaplane",
                              e.target.value,
                              filters.colorIndex,
                            )
                          }
                        />
                      </div>
                      {/* <CustomSlider
                        sliderValue={[sliderValues.userSizeMultiplier]}
                        filterKeys={["userSizeMultiplier"]}
                        updateSliderValue={updateSliderValue}
                        onSliderChangeCommitted={onSliderChangeCommitted}
                        step={0.1}
                        min={0}
                        max={3}
                      /> */}
                    </div>
                    <div>
                      {" "}
                      <div className={style.typeSection}>
                        <div className={style.typeContainer}>
                          <label>
                            <Switch
                              value="true"
                              checked={filters.scheduledService.includes(true)}
                              onChange={(e) =>
                                handleChange(e, "scheduledService")
                              }
                              disabled={airportsDisabled()}
                            />
                            <FilterTooltip id="scheduledairport">
                              <span
                                className={
                                  airportsDisabled() ? style.disabledText : ""
                                }
                              >
                                Has scheduled flights
                              </span>
                            </FilterTooltip>
                          </label>
                        </div>
                        <div className={style.typeContainer}>
                          <label>
                            <Switch
                              value="false"
                              checked={filters.scheduledService.includes(false)}
                              onChange={(e) =>
                                handleChange(e, "scheduledService")
                              }
                              disabled={airportsDisabled()}
                            />
                            <FilterTooltip id="noscheduledairport">
                              <span
                                className={
                                  airportsDisabled() ? style.disabledText : ""
                                }
                              >
                                No scheduled flights
                              </span>
                            </FilterTooltip>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className={style.typeSection}>
                      <FilterTooltip id="airportpax">
                        <TextField
                          label="Passengers/Year"
                          type="number"
                          name="airportPax"
                          value={filters.airportPax || ""}
                          onChange={(e) =>
                            updateFilters({
                              ...filters,
                              airportPax: Number(e.target.value) || 0,
                            })
                          }
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled={airportsDisabled()}
                          className={
                            airportsDisabled() ? style.disabledText : ""
                          }
                        />
                      </FilterTooltip>
                    </div>
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
            {/* ROUTES */}
            <div className={style.typeSection}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <p>Routes</p>
                </AccordionSummary>
                <AccordionDetails>
                  <div className={`${style.typeSection} ${style.gap}`}>
                    <div>
                      <div className={style.typeContainer}>
                        <label>
                          <Switch
                            value="domestic"
                            checked={filters.routeType.includes("domestic")}
                            onChange={(e) => handleChange(e, "routeType")}
                            size="small"
                          />
                          <FilterTooltip id="domestic">
                            <span>Domestic</span>
                          </FilterTooltip>
                        </label>
                        <input
                          type="color"
                          className={style.typeColorPicker}
                          value={filters.colors.domestic[filters.colorIndex]}
                          onChange={(e) =>
                            handleColorChange(
                              "domestic",
                              e.target.value,
                              filters.colorIndex,
                            )
                          }
                        />
                      </div>
                      <div className={style.typeContainer}>
                        <label>
                          <Switch
                            value="international"
                            checked={filters.routeType.includes(
                              "international",
                            )}
                            onChange={(e) => handleChange(e, "routeType")}
                            size="small"
                          />
                          <FilterTooltip id="international">
                            <span>International</span>
                          </FilterTooltip>
                        </label>
                        <input
                          type="color"
                          className={style.typeColorPicker}
                          value={
                            filters.colors.international[filters.colorIndex]
                          }
                          onChange={(e) =>
                            handleColorChange(
                              "international",
                              e.target.value,
                              filters.colorIndex,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <div className={style.typeContainer}>
                        <label>
                          <Switch
                            checked={filters.commercial}
                            onChange={(e) =>
                              updateFilters({
                                ...filters,
                                commercial: e.target.checked,
                              })
                            }
                            disabled={domesticInternationalDisabled()}
                          />
                          <FilterTooltip id="scheduledroute">
                            <span
                              className={
                                domesticInternationalDisabled()
                                  ? style.disabledText
                                  : ""
                              }
                            >
                              Scheduled
                            </span>
                          </FilterTooltip>
                        </label>
                        <input
                          type="color"
                          className={style.typeColorPicker}
                          value={
                            filters.colors.commercialRoute[filters.colorIndex]
                          }
                          onChange={(e) =>
                            handleColorChange(
                              "commercialRoute",
                              e.target.value,
                              filters.colorIndex,
                            )
                          }
                        />
                      </div>
                      <div className={style.typeContainer}>
                        <label>
                          <Switch
                            checked={filters.pso}
                            onChange={(e) =>
                              updateFilters({
                                ...filters,
                                pso: e.target.checked,
                              })
                            }
                            disabled={domesticInternationalDisabled()}
                          />
                          <FilterTooltip id="pso">
                            <span
                              className={
                                domesticInternationalDisabled()
                                  ? style.disabledText
                                  : ""
                              }
                            >
                              PSO
                            </span>
                          </FilterTooltip>
                        </label>
                        <input
                          type="color"
                          className={style.typeColorPicker}
                          value={filters.colors.psoRoute[filters.colorIndex]}
                          onChange={(e) =>
                            handleColorChange(
                              "psoRoute",
                              e.target.value,
                              filters.colorIndex,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className={style.typeSection}>
                      <div className={style.typeContainer}>
                        {" "}
                        <div className={`${style.flex} ${style.w100}`}>
                          <Switch
                            checked={filters.savedCO2Active}
                            onChange={(e) =>
                              updateFilters({
                                ...filters,
                                savedCO2Active: e.target.checked,
                              })
                            }
                            disabled={false}
                          />
                          <div className={`${style.flexBetween} ${style.w100}`}>
                            <FilterTooltip id="saved-co2">
                              <span>CO₂ Saved (kg/pax)</span>
                            </FilterTooltip>
                            <span
                              className={
                                filters.savedCO2Active ? "" : style.disabledText
                              }
                            >
                              {filters.savedCO2Value} kg
                            </span>
                          </div>
                        </div>
                      </div>
                      <CustomSlider
                        sliderValue={[sliderValues.savedCO2Value]}
                        filterKeys={["savedCO2Value"]}
                        updateSliderValue={updateSliderValue}
                        onSliderChangeCommitted={onSliderChangeCommitted}
                        min={0}
                        max={20}
                        step={1}
                        disabled={!filters.savedCO2Active}
                      />
                    </div>

                    <div className={style.typeContainer}>
                      <FilterTooltip id="saved-co2-year">
                        <TextField
                          label="Saved CO₂/year (tonnes)"
                          type="number"
                          size="small"
                          variant="outlined"
                          value={filters.savedCO2YearValue || ""}
                          onChange={(e) =>
                            updateFilters({
                              ...filters,
                              savedCO2YearValue: Number(e.target.value) || 0,
                            })
                          }
                          fullWidth
                        />
                      </FilterTooltip>
                    </div>

                    <div className={style.typeSection}>
                      <div className={style.typeContainer}>
                        <FilterTooltip id="routepax">
                          <TextField
                            label="Passengers/Year"
                            type="number"
                            name="routesPax"
                            value={filters.routesPax || ""}
                            onChange={(e) =>
                              updateFilters({
                                ...filters,
                                routesPax: Number(e.target.value) || 0,
                              })
                            }
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled={domesticInternationalDisabled()}
                            className={
                              domesticInternationalDisabled()
                                ? style.disabledText
                                : ""
                            }
                          />
                        </FilterTooltip>
                      </div>
                    </div>
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
            {/* ROUTE COMPARISON */}
            <div className={style.typeSection}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <FilterTooltip id="compare">
                    <span>Travel Comparison</span>
                  </FilterTooltip>
                </AccordionSummary>
                <AccordionDetails>
                  <div className={`${style.typeSection} ${style.gap}`}>
                    <div className={`${style.typeSection} ${style.gap}`}>
                      <div className={`${style.typeSection}`}>
                        {/* <label
                          className={`${style.typeContainer} ${style.ratioSwitchContainer}`}
                        >
                          <FilterTooltip id="driving">
                            <span>Car</span>
                          </FilterTooltip>
                          <Switch
                            value="car"
                            checked={filters.travelType.includes("car")}
                            onChange={(e) => handleChange(e, "travelType")}
                          />
                        </label>
                        <label
                          className={`${style.typeContainer} ${style.ratioSwitchContainer}`}
                        >
                          <FilterTooltip id="publictransport">
                            <span>Public Transport</span>
                          </FilterTooltip>
                          <Switch
                            value="transit"
                            checked={filters.travelType.includes("transit")}
                            onChange={(e) => handleChange(e, "travelType")}
                          />
                        </label> */}

                        <label
                          className={`${style.typeContainer} ${style.ratioSwitchContainer}`}
                        >
                          <FilterTooltip id="compareRoutes">
                            <span>Compare with car and public transport</span>
                          </FilterTooltip>
                          <Switch
                            value="true"
                            checked={filters.compareRoutes === true}
                            onChange={(e) => handleChange(e, "compareRoutes")}
                          />
                        </label>
                      </div>
                      <div>
                        <div
                          className={`${style.flex} ${style.w100} ${style.gap}`}
                        >
                          <div className={`${style.flexBetween} ${style.w100}`}>
                            <FilterTooltip id="saved">
                              <span>Time Saved</span>
                            </FilterTooltip>
                            <span>{timeConverter(filters.timeSavedValue)}</span>
                          </div>
                        </div>

                        <CustomSlider
                          sliderValue={[sliderValues.timeSavedValue]}
                          filterKeys={["timeSavedValue"]}
                          updateSliderValue={updateSliderValue}
                          onSliderChangeCommitted={onSliderChangeCommitted}
                          max={300}
                          step={5}
                        />
                      </div>
                      {/* <div>
                        <div
                          className={`${style.flex} ${style.w100} ${style.gap}`}
                        >
                          <div className={`${style.flexBetween} ${style.w100}`}>
                            <FilterTooltip id="ratio">
                              <span
                                className={
                                  disabledTextField ? style.disabledText : ""
                                }
                              >
                                Ratio
                              </span>
                            </FilterTooltip>
                            <span
                              className={
                                disabledTextField ? style.disabledText : ""
                              }
                            >
                              {filters.ratioValue}
                            </span>
                          </div>

                          <Switch
                            value="true"
                            checked={filters.ratioActive === true}
                            onChange={(e) => handleChange(e, "ratioActive")}
                            disabled={carTransitDisabled(filters.travelType)}
                          />
                        </div>
                        <CustomSlider
                          sliderValue={[sliderValues.ratioValue]}
                          filterKeys={["ratioValue"]}
                          updateSliderValue={updateSliderValue}
                          onSliderChangeCommitted={onSliderChangeCommitted}
                          max={5}
                          step={0.1}
                          disabled={carTransitDisabled(filters.travelType)}
                        />
                      </div> */}
                    </div>

                    {/* <div>
                      <div className={style.flexBetween}>
                        <FilterTooltip id="speed">
                          <span>Flight Speed</span>
                        </FilterTooltip>
                        <p>{filters.speed} km/h</p>
                      </div>
                      <CustomSlider
                        sliderValue={[sliderValues.speed]}
                        filterKeys={["speed"]}
                        updateSliderValue={updateSliderValue}
                        onSliderChangeCommitted={onSliderChangeCommitted}
                        step={5}
                        min={50}
                        max={500}
                      />
                    </div>
                    <div>
                      <div className={style.flexBetween}>
                        <FilterTooltip id="departure">
                          <span>Airport Time (Departure)</span>
                        </FilterTooltip>
                        <p>{timeConverter(filters.departure)}</p>
                      </div>
                      <CustomSlider
                        sliderValue={[sliderValues.departure]}
                        filterKeys={["departure"]}
                        updateSliderValue={updateSliderValue}
                        onSliderChangeCommitted={onSliderChangeCommitted}
                        step={5}
                        max={120}
                      />
                    </div>
                    <div>
                      <div className={style.flexBetween}>
                        <FilterTooltip id="arrival">
                          <span>Airport Time (Arrival)</span>
                        </FilterTooltip>
                        <p>{timeConverter(filters.arrival)}</p>
                      </div>
                      <CustomSlider
                        sliderValue={[sliderValues.arrival]}
                        filterKeys={["arrival"]}
                        updateSliderValue={updateSliderValue}
                        onSliderChangeCommitted={onSliderChangeCommitted}
                        step={5}
                        max={120}
                      />
                    </div> */}
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
            {/* CUSTOM AIRPORTS */}
            <div className={style.typeSection}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <FilterTooltip id="customsection">
                    <p>Custom Airports</p>
                  </FilterTooltip>
                </AccordionSummary>
                <AccordionDetails>
                  <div className={`${style.typeSection} ${style.gap}`}>
                    <div className={style.typeSection}>
                      <div className={style.typeContainer}>
                        <FilterTooltip id="customairport">
                          <label>
                            <Switch
                              value="custom"
                              checked={filters.type.includes("custom")}
                              onChange={(e) => handleChange(e, "type")}
                            />
                            Custom Airports
                          </label>
                        </FilterTooltip>
                        <input
                          type="color"
                          className={style.typeColorPicker}
                          value={filters.colors.custom[filters.colorIndex]} //default color from filters state
                          onChange={(e) =>
                            handleColorChange("custom", e.target.value)
                          }
                        />
                      </div>
                      <div className={style.typeContainer}>
                        <FilterTooltip id="customroutes">
                          <label>
                            <Switch
                              value="custom"
                              checked={filters.routeType.includes("custom")}
                              onChange={(e) => handleChange(e, "routeType")}
                              size="small"
                            />
                            Custom Routes
                          </label>
                        </FilterTooltip>
                        <input
                          type="color"
                          className={style.typeColorPicker}
                          value={filters.colors.customRoute[filters.colorIndex]} //default color from filters state
                          onChange={(e) =>
                            handleColorChange("customRoute", e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setCustomAirportManagerOpen(true)}
                    >
                      Manage Custom Airports
                    </Button>

                    {customAirportManagerOpen && (
                      <CustomAirportManagerModal
                        open={customAirportManagerOpen}
                        onClose={() => setCustomAirportManagerOpen(false)}
                      />
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
            {/* SELECTION VIEW */}
            <div className={style.typeSection}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <FilterTooltip id="customsection">
                    <p>Selection View</p>
                  </FilterTooltip>
                </AccordionSummary>
                <AccordionDetails>
                  <div className={`${style.typeSection} ${style.gap}`}>
                    {/* SELECTED VIEW TOGGLE */}
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!filters.selectionView}
                          onChange={(e) =>
                            updateFilters({
                              ...filters,
                              selectionView: e.target.checked,
                            })
                          }
                        />
                      }
                      label={
                        <FilterTooltip id="selectionView">
                          <span>Selection View</span>
                        </FilterTooltip>
                      }
                    />
                    {filters.selectionView && (
                      <div className={style.typeSection}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!!filters.selectionAirportLabels}
                              onChange={(e) =>
                                updateFilters({
                                  ...filters,
                                  selectionAirportLabels: e.target.checked,
                                })
                              }
                            />
                          }
                          label={
                            <FilterTooltip id="selectionAirportLabels">
                              <span>Show airport names</span>
                            </FilterTooltip>
                          }
                        />

                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!!filters.selectionRouteLabels}
                              onChange={(e) =>
                                updateFilters({
                                  ...filters,
                                  selectionRouteLabels: e.target.checked,
                                })
                              }
                            />
                          }
                          label={
                            <FilterTooltip id="selectionRouteLabels">
                              <span>Show route lengths</span>
                            </FilterTooltip>
                          }
                        />

                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!!filters.selectionRouteTime}
                              onChange={(e) =>
                                updateFilters({
                                  ...filters,
                                  selectionRouteTime: e.target.checked,
                                })
                              }
                            />
                          }
                          label={
                            <FilterTooltip id="selectionRouteTime">
                              <span>Show flight time</span>
                            </FilterTooltip>
                          }
                        />
                      </div>
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
            {/* COUNTRY PICKER */}
            <div className={style.typeSection}>
              <div className={style.typeContainer}>
                <Autocomplete
                  className={style.countrySelect}
                  options={countriesList}
                  getOptionLabel={(option) => option.label}
                  value={autocompleteValue} // Control the input value
                  onChange={handleCountrySelect} // Handle the selection of a country
                  inputValue={inputText} // Control the text displayed in the input
                  onInputChange={(event, newInputValue) => {
                    setInputText(newInputValue); // Update the text as the user types
                  }}
                  PopperComponent={CustomPopper} // Use the custom Popper component
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      sx={{
                        "& > img": { mr: 2, flexShrink: 0 },
                      }}
                      {...props}
                    >
                      <img
                        loading="lazy"
                        width="20"
                        //Splits option.code from f.ex. "US-TX" to just "US" to get the correct flag
                        src={`https://flagcdn.com/w20/${option.code
                          .split("-")[0]
                          .toLowerCase()}.webp`}
                        alt={`Flag of ${option.label}`}
                      />
                      {option.label} ({option.code})
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Add Country" /> // temporary fix for NEA due to short list of options for input - revert for Elfly
                  )}
                />
              </div>
              <div>
                {/* Render selected countries as list items */}
                <Box className={style.countryBox}>
                  {filters.country.map((countryCode) => {
                    const country = countriesList.find(
                      (c) => c.code === countryCode,
                    );
                    return (
                      <Chip
                        className={style.countryChip}
                        key={countryCode}
                        label={country ? country.label : countryCode}
                        onDelete={() => handleCountryRemove(countryCode)}
                        deleteIcon={<CancelIcon />}
                      />
                    );
                  })}
                  {filters.country.length > 1 && <ClearCountriesButton />}
                </Box>
              </div>
            </div>
            <div
              className={`${style.w100} ${style.flex} ${style.justCenter} ${style.marginT}`}
            >
              <RestoreDefaultValuesButton />
            </div>
            {/* ADVANCED SETTINGS */}
            <div>
              <AdvancedSettingsModal />
              <AdminMenuToggle />
            </div>
            {/* DISCLAIMER & FEEDBACK */}
            <div className={style.disclaimerFeedbackCopyrightContainer}>
              {" "}
              <div className={style.disclaimerFeedbackContainer}>
                <div className={style.disclaimerFeedbackText}>
                  <DisclaimerModal />
                </div>
                <div className={style.disclaimerFeedbackText}>
                  <FeedbackModal />
                </div>
              </div>
              {/* COPYRIGHT */}
              <div>
                <p className={style.copyright}>
                  Copyright &#169; 2025 Elfly Group
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
